"use client";

import { Monitor, AudioLines, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./dialog";
import { Button } from "./button";
import { getShareAudioGuidance } from "../../utils/captureEnvironment";

export default function DisplayAudioModal({
  isOpen,
  allowMicOnly = true,
  allowContinueAnyway = false,
  isResharing = false,
  retryFailed = false,
  captureEnvironment,
  onReshare,
  onMicOnly,
  onContinueAnyway,
  onCancel,
}) {
  const guidance = getShareAudioGuidance(captureEnvironment);
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isResharing) onCancel?.();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <DialogTitle className="text-xl font-bold text-gray-900">
            No computer audio detected
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Screen share started, but no audio was captured. Enable audio in the
            browser share dialog, or choose another option below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-2">
            <p className="font-medium flex items-center gap-2">
              <Monitor className="h-4 w-4 shrink-0" />
              How to capture computer audio
            </p>
            <ul className="list-disc list-inside space-y-1 pl-1">
              {guidance === "mobile" ? (
                <>
                  <li>Share the <strong>screen</strong> if the browser offers it</li>
                  <li>Turn on audio if the dialog shows an audio option</li>
                  <li>If no audio option appears, continue with the microphone</li>
                </>
              ) : guidance === "firefox" ? (
                <>
                  <li>
                    Choose <strong>Entire Screen</strong> or a <strong>Window</strong>
                  </li>
                  <li>
                    Turn on <strong>Share audio</strong>, then click Share
                  </li>
                </>
              ) : guidance === "safari" || guidance === "standard" ? (
                <>
                  <li>
                    Choose <strong>Entire Screen</strong> or a <strong>Window</strong>
                  </li>
                  <li>Turn on the audio option in the share dialog, then click Share</li>
                </>
              ) : (
                <>
                  <li>
                    Choose <strong>Entire screen</strong> and turn on{" "}
                    <strong>Share system audio</strong>, or choose a <strong>Window</strong>
                  </li>
                  <li>
                    Or share a <strong>tab</strong> and turn on <strong>Share tab audio</strong>
                  </li>
                </>
              )}
            </ul>
          </div>

          {retryFailed && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              Still no audio detected. Check &quot;Share tab audio&quot; or
              &quot;Share system audio&quot; in the browser dialog, then try again.
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              onClick={onReshare}
              disabled={isResharing}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              <Monitor className="mr-2 h-4 w-4" />
              {isResharing ? "Opening share dialog…" : "Share again"}
            </Button>

            {allowMicOnly && (
              <Button
                type="button"
                variant="outline"
                onClick={onMicOnly}
                disabled={isResharing}
                className="w-full"
              >
                <AudioLines className="mr-2 h-4 w-4" />
                Continue with microphone only
              </Button>
            )}

            {allowContinueAnyway && (
              <Button
                type="button"
                variant="outline"
                onClick={onContinueAnyway}
                disabled={isResharing}
                className="w-full"
              >
                Continue with current share
              </Button>
            )}

            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={isResharing}
              className="w-full text-gray-600"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
